"use client";

import React, { useState, useRef, useEffect } from "react";
import { Form, InputGroup } from "react-bootstrap";
import { Search } from "lucide-react";
import Link from "next/link";
import styles from "./BlogSearchBar.module.css";
import Image from "next/image";

interface BlogPost {
  id: string;
  slug?: string;
  title: string;
  category: string;
  image?: string;
}

interface BlogSearchBarProps {
  onSearch: (query: string) => void;
  results: BlogPost[];
  isLoading?: boolean;
  isLoadingMore?: boolean;
  hasMore?: boolean;
  onLoadMore?: () => void;
  placeholder?: string;
}

const BlogSearchBar: React.FC<BlogSearchBarProps> = ({
  onSearch,
  results,
  isLoading = false,
  isLoadingMore = false,
  hasMore = false,
  onLoadMore,
  placeholder,
}) => {
  const [query, setQuery] = useState("");
  const [showResults, setShowResults] = useState(false);
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const dropdownRef = useRef<HTMLDivElement | null>(null);
  const isLoadingMoreRef = useRef(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setQuery(value);
    onSearch(value);
  };

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(e.target as Node)
      ) {
        setShowResults(false);
      }
    };
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);

  useEffect(() => {
    const trimmed = query.trim();
    // Show dropdown if query has 2+ characters, regardless of results
    // This ensures "No posts found" message is displayed when appropriate
    if (trimmed.length > 1) {
      setShowResults(true);
    } else {
      setShowResults(false);
    }
  }, [query]);

  // Update ref when isLoadingMore changes
  useEffect(() => {
    isLoadingMoreRef.current = isLoadingMore;
  }, [isLoadingMore]);

  // Handle scroll pagination
  useEffect(() => {
    const dropdown = dropdownRef.current;
    if (!dropdown || !hasMore || !onLoadMore) {
      return;
    }

    const handleScroll = () => {
      // Prevent multiple simultaneous requests
      if (isLoadingMoreRef.current) {
        return;
      }

      const { scrollTop, scrollHeight, clientHeight } = dropdown;
      // Trigger load more when user is within 50px of the bottom
      const threshold = 50;
      const distanceFromBottom = scrollHeight - scrollTop - clientHeight;

      if (distanceFromBottom < threshold) {
        onLoadMore();
      }
    };

    dropdown.addEventListener("scroll", handleScroll);
    return () => {
      dropdown.removeEventListener("scroll", handleScroll);
    };
  }, [hasMore, onLoadMore]);

  return (
    <div className="row justify-content-center mb-5" ref={wrapperRef}>
      <div className="col-md-8 mx-auto">
        <div className={styles.searchWrapper}>
          <InputGroup>
            <InputGroup.Text className={styles.searchIcon}>
              <Search size={20} />
            </InputGroup.Text>
            <Form.Control
              type="text"
              value={query}
              onChange={handleChange}
              placeholder={placeholder || "Search blog posts..."}
              className={styles.searchInput}
              maxLength={100}
              onFocus={() => {
                const trimmed = query.trim();
                if (trimmed.length > 1) {
                  setShowResults(true);
                }
              }}
            />
          </InputGroup>

          {showResults && (
            <div ref={dropdownRef} className={styles.resultsDropdown}>
              {isLoading ? (
                <div className={styles.noResults}>Searching…</div>
              ) : results.length > 0 ? (
                <>
                  <ul className="mb-0">
                    {results.map((post) => {
                      const slug =
                        post.slug ??
                        post.title
                          .toLowerCase()
                          .trim()
                          .replace(/[^a-z0-9]+/g, "-")
                          .replace(/^-+|-+$/g, "");
                      return (
                        <li key={post.id} className={styles.resultItem}>
                          <Link
                            href={`/blog/${slug}`}
                            className="text-decoration-none"
                            onClick={() => setShowResults(false)}
                          >
                            <div className="d-flex align-items-center">
                              {post.image && (
                                <Image
                                  src={post.image}
                                  alt={post.title}
                                  className={styles.resultImage}
                                  width={50}
                                  height={50}
                                />
                              )}
                              <div>
                                <h6 className="mb-1 text-dark fw-semibold">
                                  {post.title}
                                </h6>
                                {post.category && (
                                  <small className="text-muted">
                                    {post.category}
                                  </small>
                                )}
                              </div>
                            </div>
                          </Link>
                        </li>
                      );
                    })}
                  </ul>
                  {isLoadingMore && (
                    <div className={styles.loadingMore}>
                      <div
                        className="spinner-border spinner-border-sm text-primary"
                        role="status"
                      >
                        <span className="visually-hidden">Loading more...</span>
                      </div>
                      <span className="ms-2">Loading more...</span>
                    </div>
                  )}
                </>
              ) : (
                <div className={styles.noResults}>
                  No posts found for &ldquo;{query}&rdquo;.
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BlogSearchBar;
